import { HelpCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useHelp } from "./help-context.js";
import { HelpMarkdown } from "./markdown.js";

import type { PanelHelp } from "../../help/page-guide.js";

/**
 * A small "?" affordance for a panel header. Opens a popover with the panel's
 * contextual help and a link into the full Help drawer. The panel root should
 * carry the matching `data-help-anchor` so the onboarding tour can spotlight
 * the same element.
 */
export function PanelHelpButton({ help }: { help: PanelHelp }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { openHelp } = useHelp();

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={`Help: ${help.title}`}
        aria-expanded={open}
        className="text-fg-subtle transition-colors hover:text-fg"
      >
        <HelpCircle className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={help.title}
          className="absolute right-0 top-6 z-20 w-64 rounded-md border bg-surface p-3 text-left shadow-lg"
        >
          <h3 className="text-sm font-semibold text-fg">{help.title}</h3>
          <HelpMarkdown content={help.body} className="mt-1 space-y-1 text-xs text-fg-muted" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              openHelp();
            }}
            className="mt-2 text-xs font-medium text-fg-brand hover:underline"
          >
            More help
          </button>
        </div>
      )}
    </div>
  );
}
